import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Users, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TournamentHeader } from "@/components/tournament/TournamentHeader";
import { TournamentImage } from "@/components/tournament/TournamentImage";
import { TournamentDetails } from "@/components/tournament/TournamentDetails";
import { TournamentAnnouncement } from "@/components/tournament/TournamentAnnouncement";
import { TournamentManager } from "@/components/tournament/TournamentManager";
import { SignupForm } from "@/components/signup/SignupForm";
import { DEMO_TOURNAMENT, DEMO_SIGNUPS } from "@/data/demoTournament";
import {
  getTournament,
  getTournamentDateTime,
  getParticipantsAndWaitlist,
  TournamentConfig,
  Signup,
} from "@/services/tournamentService";

function TournamentContent({ demo = false }: { demo?: boolean }) {
  const { t, language } = useLanguage();
  const [config, setConfig] = useState<TournamentConfig | null>(null);
  const [participants, setParticipants] = useState<Signup[]>([]);
  const [waitlist, setWaitlist] = useState<Signup[]>([]);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (demo) {
      setConfig(DEMO_TOURNAMENT);
      return;
    }
    getTournament().then(setConfig);
  }, [refreshKey, demo]);

  useEffect(() => {
    if (!config) return;
    if (demo) {
      setParticipants(DEMO_SIGNUPS.slice(0, DEMO_TOURNAMENT.maxPlayers));
      setWaitlist(DEMO_SIGNUPS.slice(DEMO_TOURNAMENT.maxPlayers));
      return;
    }
    getParticipantsAndWaitlist(config).then(({ participants: p, waitlist: w }) => {
      setParticipants(p);
      setWaitlist(w);
    });
  }, [config, refreshKey, demo]);


  const handleSignupComplete = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (!config) {
    return <div className="max-w-2xl mx-auto px-4 py-8 text-center text-muted-foreground">Loading...</div>;
  }

  const tournamentDate = getTournamentDateTime(config);

  // Convert signups to player format for TournamentManager
  const playersForTournament = participants.map((s) => ({
    id: s.id,
    name: s.name,
    fornavn: s.name.split(' ')[0] || s.name,
    etternavn: s.name.split(' ').slice(1).join(' ') || '',
    mobil: s.phone,
    epost: s.email,
    medlemsid: s.id,
    bekreftelsestidspunkt: s.signedUpAt,
  }));

  const spotsLeft = config.maxPlayers - participants.length;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      <TournamentHeader />

      <div className="bg-card rounded-lg shadow-lg p-6 space-y-6">
        <TournamentImage />
        
        <TournamentDetails tournamentConfig={config} />

        <div className="border-t border-border pt-4">
          <p className="text-lg font-medium flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <span className="font-bold">
              {spotsLeft > 0 ? spotsLeft : 0} {t("tournament.spots")} {config.maxPlayers}
            </span>
            {waitlist.length > 0 && <em className="text-muted-foreground"> ({waitlist.length} {t("tournament.waitlist.count")})</em>}
          </p>
        </div>

        <TournamentAnnouncement tournamentConfig={config} />

        {/* Signup Form */}
        <div className="border-t border-border pt-4">
          <SignupForm tournament={config} onSignupComplete={handleSignupComplete} />
        </div>
        
        {/* Participant List */}
        <div className="space-y-4">
          <h2 className="font-medium text-lg text-primary">{t("tournament.participants")}</h2>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                <TableHead>{language === 'no' ? 'Navn' : 'Name'}</TableHead>
                <TableHead className="text-right">{language === 'no' ? 'Telefon' : 'Phone'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {participants.length > 0 ? (
                participants.map((p, index) => (
                  <TableRow key={p.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{p.name}{p.partner?.name ? ` & ${p.partner.name}` : ''}</TableCell>
                    <TableCell className="text-right font-mono">{p.phone}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    {language === 'no' ? 'Ingen påmeldte ennå' : 'No participants yet'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Waitlist */}
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-between"
              onClick={() => setShowWaitlist(!showWaitlist)}
            >
              {waitlist.length} {t("tournament.waitlist.count")}
              {showWaitlist ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {showWaitlist && waitlist.length > 0 && (
              <Table>
                <TableBody>
                  {waitlist.map((p, index) => (
                    <TableRow key={p.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{p.name}{p.partner?.name ? ` & ${p.partner.name}` : ''}</TableCell>
                      <TableCell className="text-right font-mono">{p.phone}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* Tournament Manager - only when enough players */}
        {playersForTournament.length >= 4 && (
          <TournamentManager participants={playersForTournament} />
        )}
      </div>
    </div>
  );
}

const Index = () => {
  const location = useLocation();
  const isDemo =
    location.pathname === '/demo' || new URLSearchParams(location.search).get('demo') === '1';

  return (
    <div className="min-h-screen bg-background">
      <TournamentContent demo={isDemo} />
    </div>
  );
};


export default Index;